import React, { useState, useEffect, useRef } from "react";
import { Document, Page } from "react-pdf";
import { toast } from "react-toastify";
import CoreSpinner from "../CoreSpinner/CoreSpinner";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import { ZoomIn, ZoomOut, RotateLeft, RotateRight } from "@mui/icons-material";
import { CircularProgress, LinearProgress, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { calculateClockOrderList } from "../../../../utils/functions/boundingBox";

export const roundToNearestRotation = (angle) => {
  if (Math.abs(angle) < 45) return 0;
  const rotations = [90, 180, 270, 360]; // Rotazioni valide
  const sign = Math.sign(angle); // Mantiene il segno dell'angolo

  // Trova il valore più vicino
  const nearest = rotations.reduce((prev, curr) =>
    Math.abs(curr - Math.abs(angle)) < Math.abs(prev - Math.abs(angle))
      ? curr
      : prev
  );

  return nearest * sign; // Mantiene il segno originale
};

function PDFViewer({ url, boundingBox, docStatus }) {
  const appConfig = useSelector((state) => state.app.appConfig);
  const activeBox = appConfig?.modules?.documents?.boundingBox;

  const canvasRef = useRef(null);
  const MAX_SCALE = 4;
  const MIN_SCALE = 0.5;
  const [scale, setScale] = useState(0);
  const [view, setView] = useState(activeBox ? true : false);
  const [state, setState] = useState({
    numPages: 0,
    pageNumber: 1,
    rotation: 0,
  });
  const [totalPages, setTotalPages] = useState();
  const [initialRotation, setInitialRotation] = useState(0);

  useEffect(() => {
    if (boundingBox && boundingBox[0]) {
      setInitialRotation(roundToNearestRotation(boundingBox[0]?.rotation));
      setState({
        numPages: boundingBox[0]?.numPages,
        pageNumber: boundingBox?.page > 0 ? boundingBox[0]?.page + 1 : 1,
        rotation:
          boundingBox[0]?.rotation !== 0
            ? Math.abs(boundingBox[0]?.rotation) < 45
              ? null
              : 0
            : roundToNearestRotation(boundingBox[0]?.rotation),
      });
    }
    setScale(1.5);
  }, [url, boundingBox]);

  useEffect(() => {
    //per ricaricare al volo il componente e aggiornare le bb, brutto ma funziona
    if (activeBox) {
      setView(false);
      setTimeout(() => {
        setView(true);
        //setState((prevState) => ({ ...prevState, numPages: totalPages }));
      }, 25);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundingBox, activeBox]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setState((prevState) => ({ ...prevState, numPages }));
    setTotalPages(numPages);
  };

  const handlePrevPage = () => {
    setState((prevState) => ({
      ...prevState,
      pageNumber: prevState.pageNumber - 1,
    }));
  };

  const handleNextPage = () => {
    setState((prevState) => ({
      ...prevState,
      pageNumber: prevState.pageNumber + 1,
    }));
  };

  const handleZoomInChange = () => {
    if (scale < MAX_SCALE) setScale(scale + 0.25);
  };

  const handleZoomOutChange = () => {
    if (scale > MIN_SCALE) setScale(scale - 0.25);
  };

  const handleRotateLeft = () => {
    setState((prevState) => ({
      ...prevState,
      rotation: (prevState.rotation - 90 + 360) % 360,
    }));
  };

  const handleRotateRight = () => {
    setState((prevState) => ({
      ...prevState,
      rotation: (prevState.rotation + 90) % 360,
    }));
  };

  const rotatePoint = (x, y, angle, width, height) => {
    const radians = (Math.PI / 180) * angle;
    const centerX = width / 2;
    const centerY = height / 2;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    let nx, ny;
    if (angle === 90) {
      nx = centerY + (centerY - y);
      ny = centerX - (centerX - x);
    } else if (angle === 270) {
      nx = centerY - (centerY - y);
      ny = centerX + (centerX - x);
    } else {
      nx = cos * (x - centerX) + sin * (y - centerY) + centerX;
      ny = cos * (y - centerY) - sin * (x - centerX) + centerY;
    }

    return { x: nx, y: ny };
  };

  const calculateCanvasDimensions = (width, height, rotation) => {
    if (rotation % 180 === 0) {
      return { width, height };
    } else {
      return { width: height, height: width };
    }
  };

  const handleBoundingBox = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    // Definisci il colore e lo spessore del bordo
    ctx.fillStyle = "rgba(242, 255, 0, 0.18)";
    ctx.lineWidth = 1;

    const { width: canvasWidth, height: canvasHeight } =
      calculateCanvasDimensions(canvas.width, canvas.height, state.rotation);

    const rotationOffset = (state.rotation - initialRotation + 360) % 360;

    // Disegna i bounding boxes sul documento PDF
    if (boundingBox) {
      boundingBox
        .filter((el) => el.page === state.pageNumber - 1)
        .forEach((box) => {
          let [x1, y1, x2, y2, x3, y3, x4, y4] = box.boundingBox;

          // Calcola le nuove coordinate in base alla rotazione
          const { x: nx1, y: ny1 } = rotatePoint(
            x1 * canvasWidth,
            y1 * canvasHeight,
            rotationOffset,
            canvasWidth,
            canvasHeight
          );
          const { x: nx2, y: ny2 } = rotatePoint(
            x2 * canvasWidth,
            y2 * canvasHeight,
            rotationOffset,
            canvasWidth,
            canvasHeight
          );
          const { x: nx3, y: ny3 } = rotatePoint(
            x3 * canvasWidth,
            y3 * canvasHeight,
            rotationOffset,
            canvasWidth,
            canvasHeight
          );
          const { x: nx4, y: ny4 } = rotatePoint(
            x4 * canvasWidth,
            y4 * canvasHeight,
            rotationOffset,
            canvasWidth,
            canvasHeight
          );
          const sortedList = calculateClockOrderList([
            nx1,
            ny1,
            nx2,
            ny2,
            nx3,
            ny3,
            nx4,
            ny4,
          ]);
          const padding = 10;
          ctx.beginPath();
          ctx.moveTo(sortedList[0] - padding, sortedList[1] + padding);
          ctx.lineTo(sortedList[2] + padding, sortedList[3] + padding);
          ctx.lineTo(sortedList[4] + padding, sortedList[5] - padding);
          ctx.lineTo(sortedList[6] - padding, sortedList[7] - padding);
          ctx.lineTo(sortedList[0] - padding, sortedList[1] + padding);
          ctx.fill();
        });
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div
        style={{ width: "95%" }}
        className="flex justify-between items-center px-4 bg-stone-50 rounded-t-md"
      >
        <div className="flex items-center">
          <IconButton
            onClick={handlePrevPage}
            disabled={state.pageNumber <= 1}
            aria-label="previous-page"
          >
            <ChevronLeftIcon />
          </IconButton>
          <p style={{ margin: "0 10px" }}>
            <span>Pag {state.pageNumber}</span>
            <span>
              {" "}
              di{" "}
              {state.numPages || totalPages ? (
                state.numPages || totalPages
              ) : (
                <CircularProgress size={12} />
              )}
            </span>
          </p>
          <IconButton
            onClick={handleNextPage}
            disabled={state.pageNumber >= (state.numPages || totalPages)}
            aria-label="next-page"
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
        <div className="flex items-center">
          <IconButton
            className="mx-2"
            onClick={handleZoomOutChange}
            disabled={scale === MIN_SCALE}
          >
            <ZoomOut />
          </IconButton>
          <LinearProgress
            className="w-16"
            variant="determinate"
            value={(scale * 100) / MAX_SCALE}
            color="inherit"
          />
          <IconButton
            className="mx-2"
            onClick={handleZoomInChange}
            disabled={scale === MAX_SCALE}
          >
            <ZoomIn />
          </IconButton>
        </div>
        <div className="flex items-center">
          <IconButton className="mx-2" onClick={handleRotateLeft}>
            <RotateLeft />
          </IconButton>

          <IconButton className="mx-2" onClick={handleRotateRight}>
            <RotateRight />
          </IconButton>
        </div>
        {boundingBox && activeBox && (
          <div className="flex items-center">
            <Tooltip title="Bounding box">
              <IconButton className="mx-2" onClick={() => setView(!view)}>
                <SelectAllIcon
                  className={`${view ? "text-green-600  " : "text-gray"}`}
                />
              </IconButton>
            </Tooltip>
          </div>
        )}
        {docStatus}
      </div>
      <div
        style={{
          overflow: "scroll",
          maxHeight: "80vh",
          width: "95%",
          border: "1px solid lightgrey",
          borderRadius: 6,
        }}
      >
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<CoreSpinner />}
          onSourceError={() => {
            toast.error("errore apertura documento", {
              theme: "colored",
            });
          }}
        >
          <Page
            key={view}
            pageNumber={state.pageNumber || totalPages}
            scale={scale}
            rotate={state.rotation}
            canvasRef={view ? canvasRef : null}
            onRenderSuccess={view ? handleBoundingBox : null}
          />
        </Document>
      </div>
    </div>
  );
}

export default PDFViewer;
